import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';

import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import {
  TRADE_RULE_ACCEPT, TRADE_RULE_CANCEL, TRADE_RULE_REJECT, TRADE_RULE_STATE_PROPOSED,
} from 'src/util/constants';

import {
  buildTradeRuleActionButtons, getTradeRuleState, tradeRuleActions,
} from '../helpers';
import useCustomerOnlyRoute from '../hooks/useCustomerOnlyRoute';
import useViewerMeters from '../hooks/useViewerMeters';
import TradeRuleTimelineStepProposed from '../TradeRuleTimeline/TradeRuleTimelineStepProposed';

const getRules = (rules) => {
  if (rules) {
    const { buy, sell } = rules;
    if (buy && buy.length > 0) {
      return buy[0];
    }
    if (sell && sell.length > 0) {
      return sell[0];
    }
  }
  return {};
};

const TradeRuleCustomerShowFragment = graphql`
  fragment TradeRuleCustomerShow_viewer on Viewer @argumentDefinitions(
    id: { type: "ID!"}
  ) {
    id
    viewerUser { id type }
    rule(id: $id) {
      id
      tradeType
      state
      buyer {
        user { id email givenName familyName }
        community { id title }
        residualId
        tradePoint {
          id
          type
          meter {
            id
            identifier
            title
            property {
              id
              title
            }
          }
        }
      }
      seller {
        user { id email givenName familyName }
        community { id title }
        residualId
        tradePoint {
          id
          type
          meter {
            id
            identifier
            title
            property {
              id
              title
            }
          }
        }
      }
      clauses {
        edges {
          node {
            price
            ignoreDaylightSavings
            ignorePublicHolidays
            timezone
            publicHolidayRegion
            id
            monthsOfYear
            daysOfWeek
            timesOfDay {
              start { hours minutes }
              finish { hours minutes }
            }
          }
        }
      }
      proposedAt
      proposedBy { id email givenName familyName }
      acceptedAt
      acceptedBy { id email givenName familyName }
      rejectedAt
      rejectedBy { id email givenName familyName }
      cancelledAt
      cancelledBy { id email givenName familyName }
      closedAt
      closedBy { id email givenName familyName }
    }
  }
`;

/**
 * Builds the trade rules object (buy/sell) for the logged in user from a proposal.
 * @param {string} id - The logged in user id.
 * @param {object} proposal - The trade rule proposal.
 * @returns {object} - Trade rules segregated into buy and sell.
 */
const buildTradeRulesForUser = (id, proposal) => {
  const rules = { buy: [], sell: [] };

  if (!proposal) { return rules; }

  if (proposal.state !== TRADE_RULE_STATE_PROPOSED) { return rules; }

  const actions = [];
  // tradePoint.id may be '' (sentinel), null, or undefined — treat all as "not yet selected".
  if (proposal.buyer?.user?.id === id) {
    if (!proposal.buyer.tradePoint?.id) {
      actions.push(TRADE_RULE_REJECT);
      actions.push(TRADE_RULE_ACCEPT);
    } else {
      actions.push(TRADE_RULE_CANCEL);
    }
  }
  if (proposal.seller?.user?.id === id) {
    if (!proposal.seller.tradePoint?.id) {
      actions.push(TRADE_RULE_REJECT);
      actions.push(TRADE_RULE_ACCEPT);
    } else {
      actions.push(TRADE_RULE_CANCEL);
    }
  }

  if (proposal.seller?.user?.id === proposal.proposedBy?.id) {
    rules.sell.push({ ...proposal, actions });
  } else {
    rules.buy.push({ ...proposal, actions });
  }
  return rules;
};

/**
 * Sorts the actions in the provided trade rules object based on a predefined display order.
 * @param {object} rules - The trade rules object containing the actions to be sorted.
 * @returns {object} - The original trade rules object sorted by actions.
 */
const orderTradeRulesByActions = (rules) => {
  if (!rules) return null;
  const { actions } = rules || {};
  if (!actions) return rules;
  const displayList = [TRADE_RULE_ACCEPT, TRADE_RULE_REJECT, TRADE_RULE_CANCEL];
  actions.sort((a, b) => displayList.indexOf(a) - displayList.indexOf(b));
  return rules;
};

/**
 * TradeRuleShow functional component displays a single customer trade rule proposal
 * as a timeline, with actions to accept, reject or cancel the proposal.
 * @param {object} props - Component props.
 * @param {object} props.viewer - Viewer data from the GraphQL fragment.
 * @param {object} props.router - React Router object providing navigation capabilities.
 * @returns {React.ReactElement} The TradeRuleShow component.
 */
function TradeRuleShow({ viewer, router }) {
  const intl = useIntl();
  const environment = useRelayEnvironment();

  const viewerData = useFragment(TradeRuleCustomerShowFragment, viewer || null);

  const { rule: tradeRule, viewerUser } = viewerData || {};

  if (!tradeRule || !viewerUser) {
    return <Loading />;
  }
  const { id: userId, type: userType } = viewerUser || {};

  const { meters, metersLoading } = useViewerMeters(intl, environment, userType);
  useCustomerOnlyRoute({
    intl, router, userType, viewerUser,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Resolves the display label for a trade rule action button.
   * @param {string} action - trade rule action
   * @returns {string} the localised button label
   */
  const resolveActionLabel = (action) => {
    switch (action) {
      case TRADE_RULE_ACCEPT:
        return intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.trade_rule_action_button.accept.label', defaultMessage: 'Accept' });
      case TRADE_RULE_REJECT:
        return intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.trade_rule_action_button.reject.label', defaultMessage: 'Reject' });
      case TRADE_RULE_CANCEL:
        return intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.trade_rule_action_button.close.label', defaultMessage: 'Close' });
      default:
        return '';
    }
  };

  /**
   * Builds the trade rule action buttons for a proposed trade rule.
   * @param {object} rule - trade rule
   * @returns {React.ReactComponentElement | null} Trade rule action buttons
   */
  const tradeRuleActionButton = (rule) => tradeRuleActions(buildTradeRuleActionButtons({
    intl,
    rule,
    meters,
    metersLoading,
    isSubmitting,
    onActionStartFunc: () => setIsSubmitting(true),
    onActionFinishFunc: () => setIsSubmitting(false),
    resolveActionLabel,
  }));

  const { id } = tradeRule;

  const metersMap = {};
  meters.forEach((item) => {
    const { tradePointId } = item;
    metersMap[tradePointId] = item;
  });

  const rules = buildTradeRulesForUser(userId, tradeRule);

  const {
    tradeType, clauses, buyer, seller, proposedAt, proposedBy,
  } = tradeRule;

  const tradeRuleState = getTradeRuleState(tradeRule);
  const finalRules = getRules(rules);
  const orderedRulesByActions = orderTradeRulesByActions(finalRules);
  const buttons = tradeRuleActionButton(orderedRulesByActions);
  const pageTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.page_title', defaultMessage: 'Enosi - Trade Rule' });
  const tradeRuleHeading = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.heading.trade_rule', defaultMessage: 'Trade Rule' });
  const breadcrumbsTradeRules = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.breadcrumbs.trade_rules.label', defaultMessage: 'Trade Rules' });
  const tradeRuleContext = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.heading.context', defaultMessage: 'Rule {id}' }, { id });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbsTradeRules, path: '/trade-rules' },
          { name: id },
        ]}
      />

      <Heading title={tradeRuleHeading} context={tradeRuleContext} />

      {buttons
        && (
          <ol className="m-0 p-0">
            <TradeRuleTimelineStepProposed
              metersMap={metersMap}
              ruleId={id}
              tradeType={tradeType}
              clauses={clauses}
              buyer={buyer}
              seller={seller}
              timestamp={proposedAt}
              user={proposedBy}
              isCurrentStep={tradeRuleState === TRADE_RULE_STATE_PROPOSED}
              hasNextStep={tradeRuleState !== TRADE_RULE_STATE_PROPOSED || !!buttons}
            />
            {buttons}
          </ol>
        )}
    </>
  );
}

TradeRuleShow.propTypes = {
  viewer: PropTypes.shape({
    viewerUser: PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
    }),
    rule: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  router: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
};

export default TradeRuleShow;
